import instance from "./index";

import {
  hasProjectKnowledgeXlsxBlockedFile,
  PROJECT_KNOWLEDGE_XLSX_ERROR,
} from "./fileUpload.service";

function formatDateForPayload(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Convert timeline key dates into the create-project `projectStatus` payload shape. */
export function keyDatesToProjectStatus(keyDates) {
  const projectStatus = {};

  (Array.isArray(keyDates) ? keyDates : []).forEach((entry) => {
    const label = String(entry?.label ?? "").trim();
    const dateStr = formatDateForPayload(entry?.date);
    if (label && dateStr) {
      projectStatus[label] = dateStr;
    }
  });

  return projectStatus;
}

export const createProject = async (
{
  clientsId,
  projectName,
  modelId = 1,
  projectTypeName,
  knowledgeSharingProjectIds,
  knowledgeBank = [],
  users,
  projectStatus,} = {},
  requestConfig = {},
) => {
  const trimmedName = String(projectName ?? "").trim();
  if (!trimmedName) throw new Error("Project name is required");
  if (clientsId == null || String(clientsId).trim() === "") {
    throw new Error("Client is required");
  }
  if (hasProjectKnowledgeXlsxBlockedFile(knowledgeBank)) {
    throw new Error(PROJECT_KNOWLEDGE_XLSX_ERROR);
  }

  const formData = new FormData();
  formData.append("clientsId", String(clientsId));
  formData.append("projectName", trimmedName);
  formData.append("modelId", String(Number(modelId ?? 1)));

  const trimmedType = String(projectTypeName ?? "").trim();
  if (trimmedType) {
    formData.append("projectTypeName", trimmedType);
  }

  const knowledgeSharingIds = (
    Array.isArray(knowledgeSharingProjectIds)
      ? knowledgeSharingProjectIds
      : []
  )
    .map((id) => String(id ?? "").trim())
    .filter(Boolean);

  knowledgeSharingIds.forEach((id) => {
    formData.append("knowledgeSharingProjectId", id);
  });

  const knowledgeBankFiles = (
    Array.isArray(knowledgeBank) ? knowledgeBank : [knowledgeBank]
  ).filter(Boolean);

  knowledgeBankFiles.forEach((file) => {
    formData.append("knowledgeBank", file, file.name);
  });

  if (users && typeof users === "object" && Object.keys(users).length > 0) {
    formData.append("users", JSON.stringify(users));
  }

  if (
    projectStatus &&
    typeof projectStatus === "object" &&
    !Array.isArray(projectStatus) &&
    Object.keys(projectStatus).length > 0
  ) {
    formData.append("projectStatus", JSON.stringify(projectStatus));
  }

  try {
    const response = await instance.post("/project", formData, requestConfig);
    return response.data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to create project";
    throw new Error(message);
  }
};

export const updateProject = async (
{
  projectId,
  clientsId,
  projectName,
  modelId = 1,
  projectTypeName,
  knowledgeBank = [],
  users,
  projectStatus,
  } = {},
  requestConfig = {},
) => {
  const id = projectId ?? null;
  if (id == null || String(id).trim() === "") {
    throw new Error("Project id is required");
  }

  const trimmedName = String(projectName ?? "").trim();
  if (!trimmedName) throw new Error("Project name is required");
  if (clientsId == null || String(clientsId).trim() === "") {
    throw new Error("Client is required");
  }
  if (hasProjectKnowledgeXlsxBlockedFile(knowledgeBank)) {
    throw new Error(PROJECT_KNOWLEDGE_XLSX_ERROR);
  }

  const formData = new FormData();
  formData.append("clientsId", String(clientsId));
  formData.append("projectName", trimmedName);
  formData.append("modelId", String(Number(modelId ?? 1)));

  const trimmedType = String(projectTypeName ?? "").trim();
  if (trimmedType) {
    formData.append("projectTypeName", trimmedType);
  }

  const knowledgeBankFiles = (
    Array.isArray(knowledgeBank) ? knowledgeBank : [knowledgeBank]
  ).filter(Boolean);

  knowledgeBankFiles.forEach((file) => {
    formData.append("knowledgeBank", file, file.name);
  });

  if (users && typeof users === "object" && Object.keys(users).length > 0) {
    formData.append("users", JSON.stringify(users));
  }

  if (
    projectStatus &&
    typeof projectStatus === "object" &&
    !Array.isArray(projectStatus) &&
    Object.keys(projectStatus).length > 0
  ) {
    formData.append("projectStatus", JSON.stringify(projectStatus));
  }

  try {
    const response = await instance.put(
      `/project/updateProject/${id}`,
      formData,
      requestConfig,
    );
    return response.data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to update project";
    throw new Error(message);
  }
};


export const deleteProject = async (projectId) => {
  try {
    const response = await instance.delete(`/project/${projectId}`);
    return response.data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to delete project";
    throw new Error(message);
  }
};


export const getFileByProject = async (projectId) => {
  try {
    const response = await instance.get(`/project/getFileByProject/${projectId}`)
    return response.data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to get file by project";
    throw new Error(message);
  }
};

export const deleteProjectResource = async (projectId, fileName) => {
  const id = projectId ?? null;
  const name = String(fileName ?? "").trim();

  if (id == null || String(id).trim() === "") {
    throw new Error("Project id is required");
  }
  if (!name) {
    throw new Error("File name is required");
  }

  try {
    const encoded = encodeURIComponent(name);
    const response = await instance.delete(
      `/project/upload/resource/${id}/${encoded}`,
    );
    return response.data;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to delete project file";
    throw new Error(message);
  }
};



function getApiOrigin() {
  try {
    return new URL(instance.defaults.baseURL).origin;
  } catch {
    return "";
  }
}

function normalizeToOpenableUrl(maybeUrl) {
  if (maybeUrl == null || typeof maybeUrl !== "string") return null;
  const t = maybeUrl.trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) return `https:${t}`;
  if (t.startsWith("/")) {
    const origin = getApiOrigin();
    return origin ? `${origin}${t}` : t;
  }
  return null;
}

/**
 * Turn `viewFile` JSON into a URL suitable for `window.open`.
 * Handles common shapes: plain URL string, nested `data`, or `{ url, fileUrl, ... }`.
 */
export function resolveViewFileTargetUrl(payload) {
  if (payload == null) return null;
  if (typeof payload === "string") {
    return (
      normalizeToOpenableUrl(payload) ||
      (payload.startsWith("data:") ? payload : null)
    );
  }
  if (typeof payload !== "object") return null;

  const nested = payload.data;
  const candidates = [
    payload.url,
    payload.fileUrl,
    payload.presignedUrl,
    payload.downloadUrl,
    payload.viewUrl,
    payload.signedUrl,
    payload.filePath,
    payload.FileUrl,
    payload.Url,
    typeof nested === "string" ? nested : null,
    nested && typeof nested === "object" ? nested.url ?? nested.fileUrl : null,
  ];
  for (const c of candidates) {
    const n = normalizeToOpenableUrl(c);
    if (n) return n;
  }

  const b64 = payload.base64 ?? payload.fileContent;
  const mime =
    payload.mimeType ?? payload.contentType ?? "application/octet-stream";
  if (typeof b64 === "string" && b64.length > 0) {
    return `data:${mime};base64,${b64}`;
  }
  return null;
}

/**
 * Open the result of `viewFile` in a new tab (binary file or JSON URL payload).
 * @returns {boolean} true if a tab was opened
 */
export function openProjectFileFromViewResult(result) {
  if (result?.mode === "blob" && result.blob instanceof Blob) {
    const objectUrl = URL.createObjectURL(result.blob);
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
    return true;
  }
  if (result?.mode === "json") {
    const url = resolveViewFileTargetUrl(result.payload);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      return true;
    }
  }
  return false;
}

/** @returns {Promise<{ mode: "blob", blob: Blob } | { mode: "json", payload: unknown }>} */
export const viewFile = async (projectId, fileName) => {
  try {
    const encoded = encodeURIComponent(fileName);
    const response = await instance.get(
      `/project/viewFile/${projectId}/${encoded}`,
      { responseType: "blob" },
    );
    const blob = response.data;
    const rawHeader =
      response.headers?.["content-type"] ||
      response.headers?.["Content-Type"] ||
      "";
    const mimeBase = rawHeader.split(";")[0].trim().toLowerCase();

    if (mimeBase === "application/json" || mimeBase === "text/json") {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error("Unexpected server response");
      }
      return { mode: "json", payload };
    }

    const mimeOnly = rawHeader ? rawHeader.split(";")[0].trim() : "";
    const typedBlob =
      blob.type || !mimeOnly
        ? blob
        : new Blob([blob], {
            type: mimeOnly || "application/octet-stream",
          });

    return { mode: "blob", blob: typedBlob };
  } catch (error) {
    let message = error?.message || "Failed to view file";
    const res = error?.response;
    const data = res?.data;

    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        message =
          parsed?.message ||
          parsed?.detail ||
          parsed?.error ||
          message;
      } catch {
        // keep message
      }
    } else if (data && typeof data === "object" && !Array.isArray(data)) {
      message =
        data.message || data.detail || message;
    }

    throw new Error(message);
  }
};

const saveBlobAsFile = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

const saveUrlAsFile = (url, fileName) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadProjectFile = async (projectId, fileName) => {
  const name = String(fileName ?? "").trim();
  if (!projectId || !name) throw new Error("File is not available for download");

  const result = await viewFile(projectId, name);

  if (result?.mode === "blob" && result.blob instanceof Blob && result.blob.size > 0) {
    saveBlobAsFile(result.blob, name);
    return name;
  }

  if (result?.mode === "json") {
    const url = resolveViewFileTargetUrl(result.payload);
    if (url) {
      saveUrlAsFile(url, name);
      return name;
    }
  }

  throw new Error("The server did not return a downloadable file");
};

export const updateIsArchived = async (projectId, isArchived) => {
    try {
        const response = await instance.put(`/project/updateIsArchived/${projectId}`, null, {
            params: { isArchived },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};